import type { PlanDefinition } from "@medplum/fhirtypes";
import { type Availability, type ProgramTimeWindow, programProtocolRef, programTimeWindow } from "@repo/domain";
import { extensionUrl } from "@repo/fhir-systems";
import { TIME_WINDOW_URL } from "@repo/fhir-systems/extensions";

/**
 * Where a program's `PlanDefinition` row keeps the catalog facts the views read
 * (ADR-0021: views read FHIR rows directly and never import codecs).
 *
 * A program is a multi-day composition of protocol LINEAGES. `action` nests one
 * container per day, each holding that day's sessions; a session carries a bare
 * `definitionCanonical` and a time window, never a pinned revision, because the
 * lineage resolves to its current revision only when the program is read.
 *
 * A row missing any of them THROWS rather than reading as `""`, `NaN` or
 * `Inactive`. A substituted value is indistinguishable from a real one: every
 * broken row would share a `""` lineage, a day with no stored id would collide
 * with the next one, and a status this writer never emits would quietly join
 * the inactive list. `deriveForSync` in `./base.ts` catches this, logs the
 * resource id and drops that one row, so loud here costs a row rather than the
 * collection.
 */

export const PROGRAM_DAY_SEQUENCE_URL = extensionUrl("program-v2-day-sequence");
export const PROGRAM_SESSION_SEQUENCE_URL = extensionUrl("program-v2-session-sequence");

export interface ProgramSessionStorage {
  id: string;
  timeWindow: ProgramTimeWindow;
  /** Bare ProtocolDefinition lineage; never a pinned `url|version`. */
  protocolUrl: string;
}

export interface ProgramDayStorage {
  id: string;
  sessions: readonly ProgramSessionStorage[];
}

/** The only two statuses a program write emits; anything else is a row we did not author. */
const STORED_AVAILABILITY: Readonly<Record<string, Availability>> = { active: "Active", retired: "Inactive" };

function unreadable(resource: PlanDefinition, field: string, value: unknown): never {
  throw new Error(
    `PlanDefinition row ${resource.id ?? "<unknown>"} has no readable program ${field}${
      value === undefined ? "" : ` (${JSON.stringify(value)})`
    }`,
  );
}

export function programDefinitionUrl(resource: PlanDefinition): string {
  return resource.url ?? unreadable(resource, "url", undefined);
}

export function programDefinitionVersion(resource: PlanDefinition): number {
  const version = Number(resource.version);
  return Number.isFinite(version) ? version : unreadable(resource, "version", resource.version);
}

/**
 * The MINT date, stamped once. A correction moves `meta.lastUpdated`, so dating
 * a revision from that would date every old revision to the day of a typo fix.
 */
export function programDefinitionMintedAt(resource: PlanDefinition): string {
  return resource.date ?? unreadable(resource, "mintedAt", undefined);
}

export function programDefinitionName(resource: PlanDefinition): string {
  return resource.title ?? unreadable(resource, "name", undefined);
}

export function programDefinitionDescription(resource: PlanDefinition): string | undefined {
  return resource.description;
}

export function programDefinitionAvailability(resource: PlanDefinition): Availability {
  const stored = resource.status;
  return (stored && STORED_AVAILABILITY[stored]) ?? unreadable(resource, "availability", stored);
}

/** Days in stored order; the day NUMBER is that order and is never stored. */
export function programDefinitionDays(resource: PlanDefinition): readonly ProgramDayStorage[] {
  return (resource.action ?? []).map((day) => ({
    id: day.id ?? unreadable(resource, "day member id", undefined),
    sessions: (day.action ?? []).map((session) => {
      const canonical = session.definitionCanonical;
      const window = session.extension?.find((candidate) => candidate.url === TIME_WINDOW_URL)?.valueCode;
      return {
        id: session.id ?? unreadable(resource, "session member id", undefined),
        // Both gate on the domain's own value sets and throw on anything else.
        protocolUrl: canonical ? programProtocolRef(canonical) : unreadable(resource, "session protocol", undefined),
        timeWindow: window ? programTimeWindow(window) : unreadable(resource, "session time window", undefined),
      };
    }),
  }));
}

/**
 * Absent on a program this writer never revised, which is why it is optional
 * rather than substituted: the caller falls back to the highest id in use, and
 * a `0` here would hand out an id a retired day still holds.
 */
export function programDefinitionDayHighWaterMark(resource: PlanDefinition): number | undefined {
  return resource.extension?.find((candidate) => candidate.url === PROGRAM_DAY_SEQUENCE_URL)?.valueInteger;
}

export function programDefinitionSessionHighWaterMark(resource: PlanDefinition): number | undefined {
  return resource.extension?.find((candidate) => candidate.url === PROGRAM_SESSION_SEQUENCE_URL)?.valueInteger;
}

/**
 * Read every field the program projection will later need, and discard it.
 *
 * The projection runs inside a live query's `fn.select`, where a throw blanks
 * the whole page instead of dropping one row. Validating at sync time is what
 * stops an unreadable row ever reaching it.
 */
export function validateProgramDefinitionRow(resource: PlanDefinition): void {
  programDefinitionUrl(resource);
  programDefinitionVersion(resource);
  programDefinitionMintedAt(resource);
  programDefinitionName(resource);
  programDefinitionAvailability(resource);
  programDefinitionDays(resource);
}
